import { useEffect, useRef, useState } from 'react';
import { Alert, EmptyState, cn } from '@playerbase/ui';
import type { Playtest } from '@playerbase/types';

import { fetchPlaytest } from '../../games/playtests-data.js';
import type { SessionListFilters } from '../../games/playtest-sessions-data.js';
import { useShell } from '../../shell/use-shell.js';

import { PlaytestSatisfactionTimeline } from './playtest-satisfaction-timeline.js';
import { SessionRow } from './session-row.js';
import { SessionsFilterBar } from './sessions-filter-bar.js';
import { useSessionList } from './use-session-list.js';

const DEFAULT_FILTERS: SessionListFilters = {
  tester: null,
  state: null,
  startedAfter: null,
  buildMarkerId: null,
};

// Matches the Feedback feed's deep-link pulse duration so an Insights
// "View in feed" landing feels the same across surfaces.
const HIGHLIGHT_DURATION_MS = 2500;

interface PlaytestSessionsPaneProps {
  playtestId: string | null;
  selectedSessionId: string | null;
  onBack?: () => void;
}

export function PlaytestSessionsPane({
  playtestId,
  selectedSessionId,
  onBack,
}: PlaytestSessionsPaneProps): JSX.Element {
  const { buildMarkers, devStages } = useShell();

  const [playtest, setPlaytest] = useState<Playtest | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SessionListFilters>(DEFAULT_FILTERS);
  const { rows, loading } = useSessionList(playtestId, filters);

  // Deep-link target: when an Insights playtest mention routes here with a
  // sessionId, scroll its row into view and pulse it - the playtest-side
  // equivalent of the Feedback feed's "View in feed" highlight. `pulse`
  // drives the temporary ring; the static selection tint stays separate.
  const selectedRowRef = useRef<HTMLLIElement | null>(null);
  const [pulse, setPulse] = useState(false);
  // The session we've already scrolled to, so realtime row updates don't
  // re-fire the pulse. Latched only once the row is actually in the DOM.
  const scrolledSessionRef = useRef<string | null>(null);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setLoadError(null);
  }, [playtestId]);

  useEffect(() => {
    if (selectedSessionId === null) {
      scrolledSessionRef.current = null;
      setPulse(false);
      return undefined;
    }
    if (loading) return undefined;
    if (scrolledSessionRef.current === selectedSessionId) return undefined;
    // The row mounts a render or two after `rows` arrives; retry across a
    // few frames (~400ms) before giving up, the same budget the Feedback
    // feed uses for its scroll-target lookup.
    let cancelled = false;
    let clearTimer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = performance.now();
    const LOOKUP_TIMEOUT_MS = 400;
    const tryScroll = (): void => {
      if (cancelled) return;
      const el = selectedRowRef.current;
      if (el !== null) {
        scrolledSessionRef.current = selectedSessionId;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPulse(true);
        clearTimer = setTimeout(() => setPulse(false), HIGHLIGHT_DURATION_MS);
        return;
      }
      if (performance.now() - startedAt > LOOKUP_TIMEOUT_MS) return;
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      if (clearTimer !== null) clearTimeout(clearTimer);
    };
  }, [selectedSessionId, loading, rows]);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (playtestId === null) {
        setPlaytest(null);
        return;
      }
      const next = await fetchPlaytest(playtestId);
      if (cancelled) return;
      if (next === null) {
        setLoadError('This playtest is no longer available.');
        return;
      }
      setPlaytest(next);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [playtestId]);

  if (playtestId === null) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 py-8">
        <EmptyState
          title="Pick a playtest"
          description="Choose a playtest on the left to see its sessions."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <header className="flex items-start gap-3 border-b border-border px-5 py-3">
        {onBack !== undefined ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to playtests"
            className="md:hidden -ml-2 mt-0.5 rounded-md px-2 py-1 text-text-muted hover:bg-surface-2 hover:text-text"
          >
            ←
          </button>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Sessions
          </span>
          <h2 className="truncate font-display text-lg font-semibold text-text">
            {playtest?.name ?? 'Playtest'}
          </h2>
        </div>
      </header>
      {loadError !== null ? (
        <div className="px-5 pt-4">
          <Alert tone="danger">{loadError}</Alert>
        </div>
      ) : null}

      <div className="border-b border-border px-5 py-3">
        <SessionsFilterBar
          value={filters}
          onChange={setFilters}
          builds={buildMarkers}
          stages={devStages}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        <PlaytestSatisfactionTimeline playtestId={playtestId} />
        {loading ? (
          <div className="text-sm text-text-muted">Loading sessions…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No sessions match"
            description="When a paired tester fires this playtest's event for the first time, a session row lands here."
          />
        ) : (
          <div className="shrink-0 overflow-hidden rounded-md border border-border bg-surface">
            <div className="hidden grid-cols-12 gap-2 border-b border-border bg-surface-2 px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted md:grid">
              <span className="col-span-4">Tester</span>
              <span className="col-span-2">Build</span>
              <span className="col-span-2">State</span>
              <span className="col-span-2">Started</span>
              <span className="col-span-2">Last</span>
            </div>
            <ul className="flex flex-col divide-y divide-border">
              {rows.map((row) => {
                const isSelected = row.session.id === selectedSessionId;
                return (
                  <li
                    key={row.session.id}
                    ref={isSelected ? selectedRowRef : undefined}
                    className={cn(
                      isSelected && 'bg-accent/10',
                      // Temporary deep-link pulse, mirrors the Feedback feed.
                      isSelected && pulse && 'bg-info-bg ring-2 ring-info ring-inset',
                    )}
                  >
                    <SessionRow row={row} playtestId={playtestId} />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
